import { dayjs } from "@/lib/dayjs";
import imageCompression, {
  Options as CompressionOptions,
} from "browser-image-compression";
import { useToastStore } from "@/stores/toast.store";

const formatDate = (date?: Date | string): string => {
  if (!date) {
    return "Invalid Date";
  }
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  if (typeof date === "string") {
    date = new Date(date);
  }
  return new Intl.DateTimeFormat("vi-VN", options).format(date);
};

const formatDateWithTimezone = (
  date?: Date | string,
  timezone?: string,
): string => {
  if (!date) {
    return "Invalid Date";
  }

  const targetTimezone = timezone || "America/Los_Angeles";

  const dayjsDate = dayjs.utc(date).tz(targetTimezone);

  return dayjsDate.format("MM/DD/YYYY HH:mm");
};
function copyToClipboard(text: string): void {
  if (!navigator.clipboard) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand("copy");
      useToastStore.getState().showToast("success", "Copied", text);
    } catch (err) {
      console.error("Fallback: Copy failed", err);
    }
    document.body.removeChild(textarea);
  } else {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        useToastStore.getState().showToast("success", "Copied", text);
      })
      .catch((err) => {
        console.error("Async: Copy failed", err);
      });
  }
}
function isJsonStringified(value: string) {
  if (typeof value !== "string") return false;

  const str = value.trim();
  if (
    !(
      (str.startsWith("{") && str.endsWith("}")) ||
      (str.startsWith("[") && str.endsWith("]"))
    )
  ) {
    return false;
  }

  try {
    const parsed = JSON.parse(str);
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
}

function pascalToReadable(str: string): string {
  if (!str) return "";
  if (str.includes("_")) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return str.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

function isTheSameArray(
  arr1: (number | string)[],
  arr2: (number | string)[],
): boolean {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}
function findMissingIndexes(length: number, array: number[]): number[] {
  const fullSet = new Set<number>();
  for (let i = 0; i < length; i++) {
    fullSet.add(i);
  }

  for (const num of array) {
    fullSet.delete(num);
  }

  return Array.from(fullSet).sort((a, b) => a - b);
}

function stableStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  const replacer = (_key: string, val: any) => {
    if (val && typeof val === "object") {
      if (seen.has(val)) return;
      seen.add(val);
      const ordered: Record<string, unknown> = {};
      Object.keys(val)
        .sort()
        .forEach((k) => (ordered[k] = (val as Record<string, unknown>)[k]));
      return ordered;
    }
    return val;
  };
  try {
    return JSON.stringify(value, replacer) || "";
  } catch {
    try {
      return JSON.stringify(value) || "";
    } catch {
      return "";
    }
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

async function compressImage(
  file: File,
  options: CompressionOptions = {
    initialQuality: 0.85,
    maxWidthOrHeight: 200,
    useWebWorker: true,
    fileType: "image/webp",
  },
) {
  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error("Error compressing image:", error);
    return null;
  }
}
async function fillImageBackground(
  file: File | Blob,
  backgroundColor: string = "#FFFFFF",
  outputType: string = "image/webp",
  quality: number = 1,
): Promise<File> {
  const img = document.createElement("img");
  img.src = URL.createObjectURL(file);

  await new Promise((resolve, reject) => {
    img.onload = () => resolve(null);
    img.onerror = (err) => reject(err);
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context not available");

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(img, 0, 0);

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      outputType,
      quality,
    );
  });

  const ext = outputType.split("/")[1];
  const newFileName =
    (file instanceof File ? file.name.replace(/\.[^.]+$/, "") : "output") +
    "." +
    ext;

  return new File([blob], newFileName, { type: outputType });
}
export {
  formatDate,
  formatDateWithTimezone,
  copyToClipboard,
  isJsonStringified,
  pascalToReadable,
  isTheSameArray,
  findMissingIndexes,
  compressImage,
  stableStringify,
  deepEqual,
  fillImageBackground,
};
